import type { OfferingFrameModel } from '@sosocio/creator-types/app';
import type { OfferingModel } from '@sosocio/creator-types/database';

interface OfferingFrameProjection {
	height: number;
	left: number;
	top: number;
	width: number;
}

interface OfferingFrameProjectionOptions {
	drawHeight: number;
	drawWidth: number;
	frame: OfferingFrameModel;
	offering: Pick<OfferingModel, 'depth' | 'height' | 'width'>;
}

/**
 * Aligns a product frame with artwork whose canvas depth has been cropped away.
 *
 * Each axis maps the frame template to the corresponding artwork dimension;
 * portrait and landscape canvases have different depth compensation ratios.
 *
 * @param options Frame assets, offering dimensions, and rendered artwork size.
 * @returns Frame image size and offsets to subtract from the artwork origin.
 */
export function getOfferingFrameProjection(
	options: OfferingFrameProjectionOptions,
): OfferingFrameProjection {
	const {
		drawHeight,
		drawWidth,
		frame,
		offering,
	} = options;
	const scaleX = drawWidth / (offering.width - 2 * offering.depth);
	const scaleY = drawHeight / (offering.height - 2 * offering.depth);
	const frameScaleX = offering.width * scaleX / frame.templateModel.width;
	const frameScaleY = offering.height * scaleY / frame.templateModel.height;

	return {
		height: frame.imageModel.height * frameScaleY,
		left: frame.templateModel.x * frameScaleX + offering.depth * scaleX,
		top: frame.templateModel.y * frameScaleY + offering.depth * scaleY,
		width: frame.imageModel.width * frameScaleX,
	};
}
